import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCredits } from '../contexts/CreditContext';
import { useBrand } from '../contexts/BrandContext';
import { creditsService, type LedgerEntry } from '../services/credits';
import { formatDateTime } from '../i18n/format';

// A line's DISPLAYED state. A reserve row that hasn't settled is "en attente";
// a settled commit is "Consommation"; a refund/reaped hold is "Annulé". `grant`
// is a credit in, `expire` a credit out, `adjust` an admin tweak.
const EVENT_LABELS: Record<string, { labelKey: string; color: 'default' | 'success' | 'error' | 'warning' | 'info' }> = {
  reserve: { labelKey: 'creditLedger.event.reserve', color: 'warning' },
  commit: { labelKey: 'creditLedger.event.commit', color: 'default' },
  refund: { labelKey: 'creditLedger.event.refund', color: 'info' },
  grant: { labelKey: 'creditLedger.event.grant', color: 'success' },
  expire: { labelKey: 'creditLedger.event.expire', color: 'error' },
  adjust: { labelKey: 'creditLedger.event.adjust', color: 'default' },
};

const PAGE_SIZES = [10, 50, 100] as const;

function fmtDate(iso: string): string {
  try {
    return formatDateTime(iso, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtAmount(e: LedgerEntry): string {
  // A still-held reserve shows the held amount as a pending (negative) figure;
  // a refund nets to 0. amount is already signed for the row's current state.
  if (e.amount > 0) return `+${e.amount}`;
  return String(e.amount);
}

export default function CreditLedger() {
  const { t } = useTranslation();
  const { isAgency } = useCredits();
  const { brands } = useBrand();
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  // Cursor stack: cursors[i] is the `before` token that produced page i (page 0
  // = undefined). Lets us walk Prev/Next over the server's cursor pagination.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [pageSize, setPageSize] = useState<number>(50);
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const brandName = useCallback(
    (id?: string) => (id ? brands.find((b) => b.brandId === id)?.name ?? id : ''),
    [brands],
  );

  const loadPage = useCallback(
    async (before: string | undefined) => {
      setLoading(true);
      try {
        const res = await creditsService.getLedger({ limit: pageSize, before, brandId: brandFilter || undefined });
        setEntries(res.entries);
        setNextCursor(res.nextCursor);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, brandFilter],
  );

  // Reset to page 0 whenever the page size or brand filter changes.
  useEffect(() => {
    if (!isAgency) return;
    setCursors([undefined]);
    setPageIndex(0);
    loadPage(undefined);
  }, [isAgency, pageSize, brandFilter, loadPage]);

  if (!isAgency) {
    return <Alert severity="info">{t('creditLedger.agencyOnly')}</Alert>;
  }

  const goNext = () => {
    if (!nextCursor) return;
    setCursors((prev) => {
      const copy = [...prev];
      copy[pageIndex + 1] = nextCursor;
      return copy;
    });
    setPageIndex((i) => i + 1);
    loadPage(nextCursor);
  };

  const goPrev = () => {
    if (pageIndex === 0) return;
    const target = cursors[pageIndex - 1];
    setPageIndex((i) => i - 1);
    loadPage(target);
  };

  const exportCsv = () => {
    const header = 'timestamp,eventType,motif,brand,amount,balanceAfter,subDelta,topupDelta,reservedDelta,reason';
    const rows = entries.map((e) =>
      [
        e.timestamp,
        e.eventType,
        JSON.stringify(e.motif || ''),
        JSON.stringify(brandName(e.brandId) || ''),
        e.amount,
        e.balanceAfter ?? '',
        e.subDelta,
        e.topupDelta,
        e.reservedDelta,
        JSON.stringify(e.reason || ''),
      ].join(','),
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traaker-credits-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const showBrandColumn = !brandFilter; // hide the column when filtered to one brand

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>{t('creditLedger.history')}</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="brand-filter-label">{t('creditLedger.brand')}</InputLabel>
                <Select
                  labelId="brand-filter-label"
                  label={t('creditLedger.brand')}
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                >
                  <MenuItem value="">{t('creditLedger.allBrands')}</MenuItem>
                  {brands.map((b) => (
                    <MenuItem key={b.brandId} value={b.brandId}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel id="page-size-label">{t('creditLedger.perPage')}</InputLabel>
                <Select
                  labelId="page-size-label"
                  label={t('creditLedger.perPage')}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZES.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" variant="outlined" onClick={exportCsv} disabled={!entries.length}>
                {t('creditLedger.exportCsv')}
              </Button>
            </Stack>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('creditLedger.col.date')}</TableCell>
                <TableCell>{t('creditLedger.col.type')}</TableCell>
                <TableCell>{t('creditLedger.col.motif')}</TableCell>
                {showBrandColumn && <TableCell>{t('creditLedger.brand')}</TableCell>}
                <TableCell align="right">{t('creditLedger.col.amount')}</TableCell>
                <TableCell align="right">{t('creditLedger.col.balanceAfter')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => {
                const lblMeta = EVENT_LABELS[e.eventType];
                const lbl = lblMeta ? { label: t(lblMeta.labelKey), color: lblMeta.color } : { label: e.eventType, color: 'default' as const };
                const isPending = e.eventType === 'reserve';
                const isCancelled = e.eventType === 'refund';
                return (
                  <TableRow key={e.eventId} sx={{ opacity: isPending ? 0.7 : 1 }}>
                    <TableCell>{fmtDate(e.timestamp)}</TableCell>
                    <TableCell><Chip size="small" label={lbl.label} color={lbl.color} variant="outlined" /></TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{e.motif || e.reason || e.resourceId || '—'}</TableCell>
                    {showBrandColumn && (
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{brandName(e.brandId) || '—'}</TableCell>
                    )}
                    <TableCell
                      align="right"
                      sx={{
                        color: isCancelled
                          ? 'text.disabled'
                          : e.amount < 0
                            ? isPending ? 'warning.main' : 'error.main'
                            : e.amount > 0 ? 'success.main' : 'text.secondary',
                        textDecoration: isCancelled ? 'line-through' : 'none',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {isCancelled ? '0' : fmtAmount(e)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {typeof e.balanceAfter === 'number' ? e.balanceAfter : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!entries.length && !loading && (
                <TableRow>
                  <TableCell colSpan={showBrandColumn ? 6 : 5}>
                    <Typography variant="body2" color="text.secondary">{t('creditLedger.noMovements')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('creditLedger.page', { page: pageIndex + 1 })}</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" disabled={loading || pageIndex === 0} onClick={goPrev}>{t('creditLedger.prev')}</Button>
              <Button size="small" disabled={loading || !nextCursor} onClick={goNext}>{t('creditLedger.next')}</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
